import PropTypes from 'prop-types'
import { useState } from 'react'
import { cn } from '../../lib/cn'

/**
 * MultiSelect component for selecting multiple options
 *
 * @component
 * @example
 * <MultiSelect
 *   label="Property Types"
 *   options={[
 *     { value: 'apartment', label: 'Apartment' },
 *     { value: 'house', label: 'House' }
 *   ]}
 *   value={['apartment']}
 *   onChange={(values) => setValues(values)}
 * />
 */
export default function MultiSelect({
  label,
  name,
  options = [],
  value = [],
  onChange,
  error,
  disabled = false,
  required = false,
  help,
  className = '',
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false)
  const selectId = name || label?.toLowerCase().replace(/\s+/g, '-')

  const toggleOption = (optionValue) => {
    if (disabled) return

    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue]

    onChange(newValue)
  }

  const selectedLabels = options
    .filter(opt => value.includes(opt.value))
    .map(opt => opt.label)
    .join(', ')

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            'block w-full rounded-lg border px-4 py-2.5 text-base text-left transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-1',
            'disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500',
            error
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
          )}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${selectId}-error` : undefined}
          {...props}
        >
          <span className={selectedLabels ? 'text-gray-900' : 'text-gray-400'}>
            {selectedLabels || 'Sélectionner...'}
          </span>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg
              className={cn('h-5 w-5 text-gray-400 transition-transform', isOpen && 'rotate-180')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isOpen && !disabled && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(option.value)}
                  onChange={() => toggleOption(option.value)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-3"
                />
                <span className="text-gray-900">{option.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {help && !error && (
        <p className="mt-1 text-sm text-gray-500">{help}</p>
      )}

      {error && (
        <p
          id={`${selectId}-error`}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}

      {/* Close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

MultiSelect.propTypes = {
  /** Label */
  label: PropTypes.string,
  /** Name attribute */
  name: PropTypes.string,
  /** Options array */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  /** Selected values */
  value: PropTypes.array,
  /** Change handler receives array of values */
  onChange: PropTypes.func.isRequired,
  /** Error message */
  error: PropTypes.string,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Required field */
  required: PropTypes.bool,
  /** Help text */
  help: PropTypes.string,
  /** Additional CSS classes */
  className: PropTypes.string
}
